/**
 * I corpi in ingresso delle fasi a valle: magazzino, produzione, fine linea,
 * prelievi.
 *
 * Erano dichiarati dentro `flusso.controller.ts`, cioè in un posto dove il
 * frontend non può vederli: il banco riscriveva la stessa forma a mano a ogni
 * `fetch`, e le due copie erano libere di scostarsi in silenzio — è la stessa
 * ragione per cui le letture sono uscite dai due lati e sono finite in
 * `risposte.ts`.
 *
 * I nomi portano `Corpo` quando il corpo serve a più gesti (una cassetta si
 * scansiona, si toglie, si chiama, si rimette in coda, si versa: un corpo solo,
 * cinque rotte) e il nome del gesto quando il gesto è uno. `Corpo` non è
 * decorazione: `Collo`, `Lavorazione`, `Spedizione` e `RigaNegozio` esistono
 * già in `@errebi/domain` con un altro significato — là sono lo **stato**, qui
 * sono ciò che una schermata manda — e due nomi identici che dicono due cose
 * diverse si scambiano prima o poi in un import.
 */
import { z } from 'zod';
import { zTaglia } from './primitives.js';
import { zReparto } from './responses.js';
/** Una cassetta e basta: il corpo di mezzo magazzino e del versamento. */
export const zCorpoCassetta = z.object({ cassetta: z.string().min(1) });
/** La cassetta e il carrello su cui va, o da cui si sposta. */
export const zCorpoCarrello = z.object({
    cassetta: z.string().min(1),
    carrello: z.string().min(1),
});
/** La lavorazione che si apre, si fotografa o si chiude. */
export const zCorpoLavorazione = z.object({ cassetta: z.string().min(1), reparto: zReparto });
/**
 * Il capo rovinato: la lavorazione, più quanti capi e perché.
 *
 * Chi lo dichiara **non sta qui**: lo dice la sessione, perché è un gesto
 * attribuito e un login nel corpo sarebbe una firma che si può scrivere.
 */
export const zCapoRovinato = zCorpoLavorazione.extend({
    /**
     * Quale taglia si è rovinata.
     *
     * **Obbligatoria**, e non è pignoleria: l'avviso che ne nasce dice «va
     * reintegrato», e senza la taglia non dice cosa rifare — chi lo legge deve
     * telefonare al reparto. La cassetta le sue taglie le conosce già, quindi il
     * banco non chiede niente che non sappia: sceglie fra quelle che ha.
     */
    taglia: zTaglia,
    capi: z.number().int().positive(),
    causale: z.string().min(1),
    spiegazione: z.string().nullable().optional(),
});
/**
 * Chiudere un collo non porta più numeri.
 *
 * Il peso e il volume arrivavano dal **browser**, ed è la cosa che questo
 * schema ha smesso di permettere: un numero metrologico non deve poter essere
 * scritto da chi preme il tasto. Adesso lo cerca il server, per barcode, fra
 * le pesate che la bilancia ha mandato.
 *
 * Resta un oggetto e non sparisce: la rotta accetta un corpo vuoto, e
 * `.default({})` fa sì che anche nessun corpo sia valido.
 */
export const zChiudiCollo = z.object({}).default({});
/**
 * Il codice articolo che il magazzino accetta.
 *
 * 50 è `ART_ARTICOLO`, `nvarchar(50)` (`docs/modula/job-modula-errebi.md`). Il
 * tetto sta **qui** e non nell'adattatore perché un articolo troppo lungo non è
 * un guasto del WMS: è una richiesta malformata, e va fermata con un 400 che
 * nomina il campo invece di arrivare a un magazzino che la tronca in silenzio.
 */
export const zVariante = z.string().min(1).max(50);
/** La riga che il negozio mette in baia: articolo sfuso, a quantità. */
export const zCorpoRigaNegozioModula = z.object({
    variante: zVariante,
    capi: z.number().int().positive(),
});
/** Solo l'articolo: serve a toglierlo dalla baia. */
export const zCorpoVarianteNegozio = z.object({ variante: zVariante });
/**
 * Il prelievo dal Modula.
 *
 * L'ordine è **facoltativo**, e non per comodità: senza ordine è «dammi tre
 * nere taglia M», che è quello che una commessa chiede davvero. Con l'ordine,
 * in più, chiude la riga da negozio.
 */
export const zCorpoPrelievoNegozio = z.object({
    ordine: z.string().min(1).nullable().optional(),
    variante: zVariante,
    capi: z.number().int().positive(),
});
/** Rule 43: loading the shop shelf from the bench. */
export const zCorpoCaricoScaffale = z.object({
    variante: zVariante,
    capi: z.number().int().positive(),
});
/** Rule 45: a physical count. Zero is a real answer: the shelf is empty. */
export const zCorpoContaScaffale = z.object({
    variante: zVariante,
    contati: z.number().int().nonnegative(),
});
/** La riga da prelevare: l'ordine e l'articolo. */
export const zCorpoRigaNegozio = z.object({
    ordine: z.string().min(1),
    sku: z.string().min(1),
});
/** Il corriere è facoltativo, e con lui tutto il corpo. */
export const zChiudiSpedizione = z.object({ corriere: z.string().optional() }).default({});
/** Il turno si apre o si chiude: un booleano, ma dichiarato. */
export const zCambiaTurno = z.object({ aperto: z.boolean() });
/**
 * The consignee as the carriers accept it, checked on the trimmed text. The
 * body takes the largest limit among carriers (the address: 40, GLS); the
 * domain's `consigneeProblems` (`packages/domain/src/flow/carrier.ts`) applies
 * the carrier's own, so a body can pass here and still be sent to fix.
 */
const zTestoTagliato = (min, max) => z.string().trim().min(min).max(max);
const zDue = z.string().regex(/^[A-Za-z]{2}$/);
export const zDestinatario = z.object({
    nome: zTestoTagliato(1, 70),
    indirizzo: zTestoTagliato(6, 40),
    cap: zTestoTagliato(1, 9),
    citta: zTestoTagliato(1, 35),
    provincia: z.union([zDue, z.literal('')]).nullable(),
    nazione: zDue,
    telefono: z.string().nullable(),
    email: z.string().nullable(),
});
/** A weight written by hand: BRT takes format 5.1, so 999.9 is the ceiling. */
export const zPesoAMano = z.object({ pesoKg: z.number().positive().max(999.9) });
/** A manual-mode search hit: where the order was found and what to show. */
export const zRisultatoManuale = z.object({
    provenienza: z.enum(['flusso', 'spedito', 'deconetwork']),
    ordine: z.string(),
    idDeco: z.number().nullable(),
    cliente: z.string(),
    consegna: z.string().nullable(),
    righe: z.number().int(),
    capi: z.number().int(),
    ritiro: z.boolean(),
    avviso: z.string().nullable(),
    /**
     * Whether «Apri» can take it by hand: false for an order of the automatic
     * flow (it has crates or is in production) and for one already shipped.
     */
    apribile: z.boolean(),
});
/** What the manual search answers: the hits, and a sentence when something went wrong. */
export const zRicercaManuale = z.object({
    risultati: z.array(zRisultatoManuale),
    avviso: z.string().nullable(),
});
/**
 * Open an order by hand. `idDeco` picks the DecoNetwork document among those
 * with that number; null when DecoNetwork gives the document without an id.
 */
export const zApriManuale = z.object({
    ordine: z.string().trim().min(1),
    idDeco: z.number().int().nullable(),
});
//# sourceMappingURL=flow.js.map